<template>
    <div class="widget-layout-config">
        <RedesignFormItem v-if="isHtmlEditor">
            <VcCheckbox
                :model-value="localModel.showLastUpdateDate ?? false"
                @change="localModel.showLastUpdateDate = $event"
            >
                {{ t('widget.displayLastUpdateDate') }}
            </VcCheckbox>
        </RedesignFormItem>
        <RedesignFormItem v-if="isNoteListWidget">
            <VcCheckbox
                :model-value="localModel.displayWidgetTitle ?? false"
                @change="localModel.displayWidgetTitle = $event"
            >
                {{ t('widget.displayWidgetTitle') }}
            </VcCheckbox>
        </RedesignFormItem>
        <RedesignFormItem
            v-if="isQueueDashboardWidget"
        >
            <div class="pb-2">
                <RedesignFormLabel
                    :label="t('widget.config.cardTitleFontSize')"
                    class="pb-2"
                />
                <VcInputNumber
                    v-model="localModel.titleFontSize"
                    :max-value="cardTitleFontSizes.max"
                    :min-value="cardTitleFontSizes.min"
                    :step="1"
                    placeholder="0"
                />
            </div>
            <div>
                <RedesignFormLabel
                    :label="t('widget.config.cardValueFontSize')"
                    class="pb-2"
                />
                <VcInputNumber
                    v-model="localModel.valueFontSize"
                    :max-value="cardValueFontSizes.max"
                    :min-value="cardValueFontSizes.min"
                    :step="1"
                    placeholder="0"
                />
            </div>
        </RedesignFormItem>
        <RedesignFormItem
            v-if="isClockAndDateWidget"
        >
            <div class="pb-2">
                <RedesignFormLabel
                    :label="t('widget.config.cardTitleFontSize')"
                    class="pb-2"
                />
                <VcInputNumber
                    v-model="localModel.titleFontSize"
                    :max-value="cardTitleFontSizes.max"
                    :min-value="cardTitleFontSizes.min"
                    :step="1"
                    placeholder="0"
                />
            </div>
            <div class="pb-2">
                <RedesignFormLabel
                    :label="t('widget.config.cardValueFontSize')"
                    class="pb-2"
                />
                <VcInputNumber
                    v-model="localModel.valueFontSize"
                    :max-value="cardValueFontSizes.max"
                    :min-value="cardValueFontSizes.min"
                    :step="1"
                    placeholder="0"
                />
            </div>
        </RedesignFormItem>
        <RedesignFormItem
            v-if="isQueueGauge"
            :label="t('widget.statusLabelFontSize')"
        >
            <VcInputNumber
                v-model="localModel.labelFontSize"
                :max-value="textFontSizes.max"
                :min-value="textFontSizes.min"
                :step="1"
                placeholder="0"
            />
        </RedesignFormItem>
        <RedesignFormItem
            v-if="isPieWidget"
        >
            <RedesignFormLabel
                :label="t('widget.statusLabelFontSize')"
                class="pb-2"
            />
            <VcInputNumber
                v-model="localModel.labelFontSize"
                :max-value="textFontSizes.max"
                :min-value="textFontSizes.min"
                :step="1"
                placeholder="0"
            />
            <div class="w-full flex flex-col gap-1 pt-3">
                <RedesignFormLabel
                    :label="t('widget.dataLabelsColor')"
                    class="pb-2"
                />
                <VcColorPicker
                    v-model="localModel.dataLabelsColor"
                    :color-list="predefinedColors"
                    size="small"
                />
            </div>
        </RedesignFormItem>
        <RedesignFormItem v-if="isTableWidget">
            <VcCheckbox
                :model-value="localModel.isWrap ?? false"
                @change="localModel.isWrap = $event"
            >
                {{ t('widget.wrapText') }}
            </VcCheckbox>
        </RedesignFormItem>
        <RedesignFormItem>
            <VcCheckbox
                :model-value="localModel.hideContainer ?? false"
                @change="localModel.hideContainer = $event"
            >
                {{ t('widget.hideContainer') }}
            </VcCheckbox>
        </RedesignFormItem>
        <RedesignFormItem :label="t('widget.widgetPadding')">
            <VcInputNumber
                v-model="localModel.padding"
                :step="1"
                type="number"
                :min="0"
                placeholder="0"
            />
        </RedesignFormItem>
        <template v-if="isTableWidget">
            <div class="mb-3">
                <WidgetTableColumnsAlignment
                    :alignment="tableColumnsAlignment"
                    :has-column-specific-alignment="localModel.hasColumnSpecificAlignment ?? false"
                    @update:alignment="(val) => tableColumnsAlignment = val"
                />
            </div>
            <RedesignTableHeadersFontSize
                v-model="localModel.tableHeaderFontSize"
            />
            <RedesignTableFontSize
                v-model="localModel.tableContentFontSize"
            />
            <RedesignTableRowPadding
                v-model="localModel.tableRowPadding"
            />
        </template>
        <RedesignFormItem
            v-if="isFunnelChartWidget"
            :label="t('widget.config.maxPendingCallDisplay')"
        >
            <vc-input-number
                v-model="localModel.maxPendingCallDisplay"
                :min-value="0"
                :step="1"
                type="number"
                placeholder="50"
            />
        </RedesignFormItem>
        <template v-if="isExtensionCardStatisticsWidget && settingsModel">
            <RedesignFormItem
                :label="t('settings.extension.agentNameFontSize')"
            >
                <VcInputNumber
                    v-model="settingsModel.agentNameFontSize"
                    :placeholder="String(realTimeSettings.agentNameFontSize)"
                    :step="1"
                    :min-value="fontSizeRange.min"
                    :max-value="fontSizeRange.max"
                />
            </RedesignFormItem>
            <RedesignFormItem
                :label="t('settings.extension.timerFontSize')"
            >
                <VcInputNumber
                    v-model="settingsModel.timerFontSize"
                    :placeholder="String(realTimeSettings.timerFontSize)"
                    :step="1"
                    :min-value="fontSizeRange.min"
                    :max-value="fontSizeRange.max"
                />
            </RedesignFormItem>
            <RedesignFormItem
                v-if="localModel.settings && 'callInfoFontSize' in localModel.settings"
                :label="t('settings.extension.callInfoFontSize')"
            >
                <VcInputNumber
                    v-model="localModel.settings.callInfoFontSize"
                    :placeholder="String(realTimeSettings.callInfoFontSize)"
                    :step="1"
                    :min-value="fontSizeRange.min"
                    :max-value="fontSizeRange.max"
                />
            </RedesignFormItem>
            <RedesignFormItem
                :label="t('widget.statistic.avg.ratio.positive')"
            >
                <VcInputNumber
                    v-model="settingsModel.positivePerformanceThreshold"
                    :step="1"
                    :min-value="0"
                    :max-value="100"
                    :placeholder="String(realTimeSettings.positivePerformanceThreshold)"
                />
            </RedesignFormItem>
            <VcCheckbox
                :model-value="settingsModel.hideStatistics ?? false"
                @change="settingsModel.hideStatistics = $event"
            >
                <span class="main-text-color">
                    {{ t('settings.extension.hideStatistics') }}
                </span>
            </VcCheckbox>
        </template>
        <RedesignWidgetColors
            v-model="colorsModel"
            :available-colors="availableColors"
        />
    </div>
</template>

<script setup lang="ts">
import { WidgetLayout } from '@/types/dashboard'
import RedesignFormItem from '@/components/RedesignBaseComponents/Form/RedesignFormItem.vue'
import { predefinedColors } from '@/views/DashboardSettings/LayoutManagement/layout-management'
import RedesignFormLabel from '@/components/RedesignBaseComponents/Form/RedesignFormLabel.vue'
import RedesignTableFontSize from '@/components/RedesignComponents/Widgets/Common/RedesignTableFontSize.vue'
import RedesignTableHeadersFontSize from '@/components/RedesignComponents/Widgets/Common/RedesignTableHeadersFontSize.vue'
import RedesignTableRowPadding from '@/components/RedesignComponents/Widgets/Common/RedesignTableRowPadding.vue'
import WidgetTableColumnsAlignment from '@/components/RedesignComponents/Widgets/Common/WidgetTableColumnsAlignment.vue'
import { realTimeSettings } from '@/enum/defaultWidgetSettings'
import RedesignWidgetColors from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetColors.vue'
import { computed } from 'vue'
import type { TableAlignmentType } from '@/types/alignment'
import { VcColorPicker, VcInputNumber, VcCheckbox } from '@voicenter-team/voicenter-ui-plus'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const localModel = defineModel<WidgetLayout>({ required: true })

const props = defineProps<{
    isHtmlEditor?: boolean
    isNoteListWidget?: boolean
    isQueueDashboardWidget?: boolean
    isClockAndDateWidget?: boolean
    isQueueGauge?: boolean
    isPieWidget?: boolean
    isTableWidget?: boolean
    isFunnelChartWidget?: boolean
    isChartWidget?: boolean
    isExtensionCardStatisticsWidget?: boolean
    availableColors?: Record<string, string>
    widgetDataType?: number
}>()


const colorsModel = computed({
    get () {
        return localModel.value.colors
    },
    set (val) {
        localModel.value = {
            ...localModel.value,
            colors: { ...val }
        }
    }
})
const settingsModel = computed({
    get () {
        return localModel.value.settings
    },
    set (val) {
        localModel.value = {
            ...localModel.value,
            settings: { ...val }
        }
    }
})
const tableColumnsAlignment = computed({
    get () {
        return localModel.value?.tableColumnsAlignment
    },
    set (val: TableAlignmentType | undefined) {
        if (val === undefined) return

        const displayQueuesAsRow = localModel.value?.displayQueuesAsRow
        const columnsAsQueuesConfig = localModel.value?.columnsAsQueuesConfig || {}
        const columnsObjectConfig = localModel.value?.columnsObjectConfig || {}
        const useQueuesConfig = displayQueuesAsRow === true ||
            (displayQueuesAsRow === undefined && Object.keys(columnsAsQueuesConfig).length > 0)

        const configKey = useQueuesConfig ? 'columnsAsQueuesConfig' : 'columnsObjectConfig'
        const currentConfig = useQueuesConfig ? columnsAsQueuesConfig : columnsObjectConfig

        const updatedConfig = Object.fromEntries(
            Object.entries(currentConfig).map(([ key, config ]) => [
                key,
                {
                    ...config,
                    align: val
                }
            ])
        )

        localModel.value = {
            ...localModel.value,
            tableColumnsAlignment: val,
            hasColumnSpecificAlignment: false,
            [configKey]: updatedConfig
        }
    }
})
const fontSizeRange = {
    min: 12,
    max: 48
}
const cardValueFontSizes = {
    min: 24,
    max: 128,
}
const cardTitleFontSizes = {
    min: 12,
    max: 64,
}
const textFontSizes = {
    min: 12,
    max: 64,
}
</script>
